import React, { useState, useEffect, useMemo } from 'react';
import {
  ShieldCheck, Loader2, CheckCircle2, AlertTriangle, ScanLine,
  Fingerprint, Landmark, FileCheck, Search, CreditCard, UserCheck, Lock
} from 'lucide-react';

const ICONS = {
  document_ocr: ScanLine,
  liveness: Fingerprint,
  face_match: UserCheck,
  registry: Landmark,
  tax: FileCheck,
  aml: Search,
  credit_bureau: CreditCard,
};

const STEP_MS = 820;

// Ultra-pro animated identity + credit verification playback (simulated engine output).
export const IdentityVerification = ({ result, onComplete }) => {
  const sequence = useMemo(() => {
    const seq = [];
    ['applicant', 'guarantor'].forEach((subj) => {
      const r = result?.verification?.[subj];
      if (r) r.checks.forEach((c) => seq.push({ ...c, subject: subj }));
    });
    return seq;
  }, [result]);

  const [step, setStep] = useState(0);

  useEffect(() => {
    if (step < sequence.length) {
      const t = setTimeout(() => setStep((s) => s + 1), STEP_MS);
      return () => clearTimeout(t);
    }
    const done = setTimeout(() => onComplete && onComplete(), 900);
    return () => clearTimeout(done);
  }, [step, sequence.length, onComplete]);

  const progress = Math.round((step / Math.max(1, sequence.length)) * 100);
  const current = sequence[Math.min(step, sequence.length - 1)];
  const finished = step >= sequence.length;

  const subjectLabel = (s) => (s === 'applicant' ? 'Solicitante' : 'Aval / Co-deudor');

  const renderSubject = (subj) => {
    const r = result?.verification?.[subj];
    if (!r) return null;
    const baseIndex = subj === 'guarantor' ? (result.verification.applicant?.checks?.length || 0) : 0;
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`verify-panel-${subj}`}>
        <div className="flex items-center justify-between mb-4">
          <p className="font-bold text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>{subjectLabel(subj)}</p>
          <span className="text-xs text-slate-400 flex items-center gap-1"><Lock className="w-3 h-3" /> {r.country}</span>
        </div>
        <div className="space-y-2.5">
          {r.checks.map((c, i) => {
            const globalIdx = baseIndex + i;
            const state = globalIdx < step ? 'done' : globalIdx === step ? 'active' : 'pending';
            const Icon = ICONS[c.id] || ShieldCheck;
            const passed = c.status === 'passed';
            return (
              <div
                key={c.id}
                className={`flex items-center gap-3 p-2.5 rounded-xl transition-all duration-300 ${
                  state === 'active' ? 'bg-[#0A2463]/5 scale-[1.01]' : state === 'done' ? 'opacity-100' : 'opacity-40'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${state === 'pending' ? 'bg-slate-100 text-slate-400' : 'bg-[#0A2463] text-white'}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{c.label}</p>
                  <p className="text-[11px] text-slate-400 truncate">{c.authority}</p>
                </div>
                <div className="flex-shrink-0 w-16 text-right">
                  {state === 'done' ? (
                    passed ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                        <CheckCircle2 className="w-4 h-4" /> {Math.round(c.confidence)}%
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-500 text-xs font-semibold">
                        <AlertTriangle className="w-4 h-4" /> revisar
                      </span>
                    )
                  ) : state === 'active' ? (
                    <Loader2 className="w-4 h-4 text-[#0A2463] animate-spin inline" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-slate-200 inline-block" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Extracted data reveal */}
        {step > baseIndex + r.checks.length - 1 && (
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs animate-in fade-in">
            <div><span className="text-slate-400">Nombre: </span><span className="font-semibold text-slate-700">{r.extracted.full_name || '—'}</span></div>
            <div><span className="text-slate-400">{r.extracted.national_id_label}: </span><span className="font-semibold text-slate-700">{r.extracted.national_id || '—'}</span></div>
            <div className="col-span-2"><span className="text-slate-400">Documento: </span><span className="font-semibold text-slate-700">{r.extracted.document_type}</span></div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="identity-verification">
      {/* Header + progress */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#0A2463] mb-4 relative">
          <ShieldCheck className="w-8 h-8 text-white" />
          {!finished && <span className="absolute inset-0 rounded-2xl border-2 border-[#E91E8C] animate-ping opacity-40" />}
        </div>
        <h3 className="text-xl font-bold text-[#0A2463]" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {finished ? 'Verificación completada' : 'Verificando identidad y capacidad de pago'}
        </h3>
        <p className="text-sm text-slate-500 mt-1 h-5">
          {finished ? 'Procesando resultado...' : (current ? `${current.authority} · ${current.label}` : 'Conectando...')}
        </p>
        <div className="max-w-md mx-auto mt-4">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#0A2463] to-[#E91E8C] transition-all duration-500 rounded-full" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-slate-400 mt-2">{progress}% · Conexión cifrada de extremo a extremo</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {renderSubject('applicant')}
        {renderSubject('guarantor')}
      </div>
    </div>
  );
};

export default IdentityVerification;
